import React from "react";
import {
  BoardLayout,
  CountBadge,
  GridCell,
  GridCornerCell,
  GridHeaderCell,
  GridPanel,
  GridTable,
  HourLabelCell,
  MasterCellStack,
  ShowBoxCurator,
  ShowBoxRoot,
  ShowBoxTitle,
  Sidebar,
  SidebarEmpty,
  SidebarHeader,
  SidebarTitle,
  StatusText,
  WeekCornerCell,
  WeekLabelCell,
} from "./ScheduleBoard.styles";
import {
  DAYS,
  MasterGridItem,
  WEEKS_PER_BLOCK,
  WEEK_COUNT,
  blockStartHourLabel,
  blockTimeRange,
  cellId,
} from "./types";

interface MasterScheduleGridProps {
  // cellId -> shows scheduled in that day/week slot (already deduped).
  cells: Record<string, MasterGridItem[]>;
  loading: boolean;
  // "White blocks" mode: render blocks white with black text.
  whiteBlocks?: boolean;
}

const MasterScheduleGrid = ({
  cells,
  loading,
  whiteBlocks,
}: MasterScheduleGridProps): React.JSX.Element => {
  const hasEvents = Object.keys(cells).length > 0;

  const gridBody =
    loading && !hasEvents ? (
      <StatusText>Loading master schedule…</StatusText>
    ) : (
      <GridTable>
        <GridCornerCell />
        <WeekCornerCell />
        {DAYS.map((day) => (
          <GridHeaderCell key={day}>{day}</GridHeaderCell>
        ))}
        {Array.from({ length: WEEK_COUNT }, (_, weekIndex) => {
          const monthStart = weekIndex % WEEKS_PER_BLOCK === 0;
          return (
            <React.Fragment key={weekIndex}>
              {monthStart ? (
                <HourLabelCell
                  $span={WEEKS_PER_BLOCK}
                  title={blockTimeRange(weekIndex)}
                >
                  {blockStartHourLabel(weekIndex)}
                </HourLabelCell>
              ) : null}
              <WeekLabelCell
                $monthStart={monthStart}
                title={`${blockTimeRange(weekIndex)} · W${
                  (weekIndex % WEEKS_PER_BLOCK) + 1
                }`}
              >
                W{(weekIndex % WEEKS_PER_BLOCK) + 1}
              </WeekLabelCell>
              {DAYS.map((_, dayIndex) => {
                const id = cellId(dayIndex, weekIndex);
                const items = cells[id];
                return (
                  <GridCell
                    key={id}
                    $occupied={Boolean(items?.length)}
                    $monthStart={monthStart}
                  >
                    {items?.length ? (
                      <MasterCellStack>
                        {items.map((item) => (
                          <ShowBoxRoot
                            key={item.key}
                            as="div"
                            $readOnly
                            $state={item.state}
                            $whiteBlocks={whiteBlocks}
                            title={
                              item.subtitle
                                ? `${item.title} — ${item.subtitle}`
                                : item.title
                            }
                          >
                            <ShowBoxTitle>
                              {item.title}
                              {item.count > 0 ? (
                                <CountBadge>({item.count})</CountBadge>
                              ) : null}
                            </ShowBoxTitle>
                            {item.subtitle ? (
                              <ShowBoxCurator>{item.subtitle}</ShowBoxCurator>
                            ) : null}
                          </ShowBoxRoot>
                        ))}
                      </MasterCellStack>
                    ) : null}
                  </GridCell>
                );
              })}
            </React.Fragment>
          );
        })}
      </GridTable>
    );

  // Mirror the board's two-column layout so the grid sits in the exact same
  // position; the sidebar column is reserved but read-only (no placement here).
  return (
    <BoardLayout>
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>Master Schedule</SidebarTitle>
        </SidebarHeader>
        <SidebarEmpty>
          Read-only view of the current master schedule. Switch to Board to
          plan.
        </SidebarEmpty>
      </Sidebar>
      <GridPanel>{gridBody}</GridPanel>
    </BoardLayout>
  );
};

export default MasterScheduleGrid;
